// Deck price totals computed from the local card-metadata batch (which
// includes Scryfall-snapshot price_usd per card). Kept as a tiny pure
// module so both DeckView and the DeckDetail header can share the calc.

import type { CardMeta, DeckEntry } from "./api";

export interface DeckCost {
  usd: number;
  /** Count of individual *copies* (not unique cards) for which we have
   *  no price. Lets the caller show a "≥ $N" hint. Basic lands do NOT
   *  count as missing — they're $0 by convention. See BASIC_LANDS. */
  missing: number;
}

// Mirror of the server-side list in web/server/routes/decks.ts. Keep in
// sync: basics are $0, not "unpriced", so ≥ prefix is reserved for real
// cards the DB didn't have a price for.
const BASIC_LANDS = new Set([
  "plains", "island", "swamp", "mountain", "forest", "wastes",
  "snow-covered plains", "snow-covered island", "snow-covered swamp",
  "snow-covered mountain", "snow-covered forest",
]);

export function deckCost(
  entries: DeckEntry[],
  meta: Record<string, CardMeta> | undefined,
): DeckCost | null {
  if (!meta) return null;
  let usd = 0;
  let missing = 0;
  for (const e of entries) {
    const key = e.name.toLowerCase();
    if (BASIC_LANDS.has(key)) continue; // $0, not missing
    const m = meta[key];
    if (m && typeof m.price_usd === "number") {
      usd += m.price_usd * e.count;
    } else {
      missing += e.count;
    }
  }
  return { usd, missing };
}

export function totalDeckCost(
  mainboard: DeckEntry[],
  sideboard: DeckEntry[],
  meta: Record<string, CardMeta> | undefined,
): DeckCost | null {
  if (!meta) return null;
  const main = deckCost(mainboard, meta)!;
  const side = deckCost(sideboard, meta)!;
  return { usd: main.usd + side.usd, missing: main.missing + side.missing };
}

export function formatCost(cost: DeckCost): string {
  const prefix = cost.missing > 0 ? "≥ " : "";
  return `${prefix}$${cost.usd.toFixed(0)}`;
}
